import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import InvitePage, { hasValidInvite } from './pages/InvitePage'
import LoginPage from './pages/LoginPage'
import OAuthCallbackPage from './pages/OAuthCallbackPage'
import LevelSelectPage from './pages/LevelSelectPage'
import GamePage from './pages/GamePage'
import { useAuthStore } from './stores/authStore'
import { authAPI } from './services/api'

function App() {
  const { isAuthenticated, signOut, setSessionChecked } = useAuthStore()

  // Validate session on app load to sync localStorage with server cookies
  useEffect(() => {
    if (isAuthenticated) {
      authAPI
        .getMe()
        .then(() => setSessionChecked(true))
        .catch(() => {
          console.warn('Session validation failed - clearing local auth state')
          signOut()
          setSessionChecked(true)
        })
    } else {
      setSessionChecked(true)
    }
  }, []) // Run once on mount

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/login" element={<InviteGate><LoginPage /></InviteGate>} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/" element={<ProtectedRoute><LevelSelectPage /></ProtectedRoute>} />
        <Route path="/game/:puzzleId" element={<ProtectedRoute><GamePage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

// Gate: must have invite code before seeing login page
function InviteGate({ children }: { children: React.ReactNode }) {
  if (!hasValidInvite()) {
    return <Navigate to="/invite" replace />
  }
  return <>{children}</>
}

// Protected route: authenticated users pass through, others go to login (or invite)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, sessionChecked } = useAuthStore()

  if (!sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-600 text-xl">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // If they have an invite code, go to login; otherwise invite page
    if (hasValidInvite()) {
      return <Navigate to="/login" replace />
    }
    return <Navigate to="/invite" replace />
  }

  return <>{children}</>
}

export default App
